import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ButtonProps,
  Icon,
  Popup,
  Image,
  Modal,
  Input,
  Divider,
  Item,
  ModalHeader,
  ModalContent,
  ModalActions,
  Dropdown,
  DropdownMenuProps,
  DropdownProps,
  List,
  ListItem,
  ListList,
  Dimmer,
  Loader,
} from "semantic-ui-react";

// component
import SegmentTargetTree from "react-lib/apps/segment/SegmentTargetTree";
import CardSegmentEditContent from "react-lib/apps/segment/CardSegmentEditContent";
import ThreePanelSplitView from "./ThreePanelSplitView";
import { State } from "react-lib/apps/segment/sequence/SegmentTarget";
import {
  BranchType,
  ListDataType,
  SegmentData,
  SegmentItem,
  ListSegmentTreesType,
  typeAction,
} from "./segmentTreeType";
import moment from "moment";
import { Timestamp } from "firebase/firestore";
import SnackMessage from "../common/SnackMessage";

const ICON = {
  addSegment: "static/images/segmentTarget/new-segment.png",
};

type ModalType = {
  type: typeAction;
  id?: string;
  description?: string;
};

const STYLES = {
  nodeContainer: {
    cursor: "pointer",
    position: "relative",
    background: "#fff",
    display: "flex",
    padding: "12px 14px",
    justifyContent: "space-between",
    alignItems: "center",
  } as CSSProperties,
  nodeLabel: {
    justifyContent: "space-between",
    cursor: "pointer",
    display: "flex",
    alignItems: "baseline",
    borderRadius: "2px",
    padding: "8px 4px",
    width: "100%",
  } as CSSProperties,
};

type CardSegmentTargetManageProps = {
  runSequence: any;
  setProp: any;
  userId: number | string;
  selectedDivisionId: number;
  languageUX: "en" | "th";
} & Pick<State, "SegmentTargetSequence">;

export const UploadRuleTemplates = "UPLOAD_RULE_TEMPLATES_LOADING";

const CardSegmentTargetManage = (props: CardSegmentTargetManageProps) => {
  // Modal Edit
  const [openModelTree, setOpenModelTree] = useState<ModalType | undefined>(undefined);
  const [disabledBtnModelTree, setDisabledBtnModelTree] = useState(false);

  // Modal Add
  const [openModelAddTree, setOpenModelAddTree] = useState<any>(undefined);
  const [addTreeId, setAddTreeId] = useState("");
  const [disabledBtnModelAddTree, setDisabledBtnModelAddTree] = useState(false);

  // Modal Remove
  const [openModelDeleteAllChild, setOpenModelDeleteAllChild] = useState<any>(false);
  const [disabledBtnModelDeleteAllChild, setDisabledBtnModelDeleteAllChild] = useState(false);

  // Modal Content
  const [modalParam, setModalParam] = useState<any>(undefined);

  // Modal Variable
  const [segmentName, setSegmentName] = useState("");
  const [segmentDescription, setSegmentDescription] = useState("");

  // --- Age matching helpers (component-level so they can be used in both useMemo and render) ---
  const parseOptAge = (optAge: string) => {
    if (!optAge || optAge === "All") return null;
    const rangeMatch = optAge.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (rangeMatch)
      return { operator: "range", min: Number(rangeMatch[1]), max: Number(rangeMatch[2]) };
    const opMatch = optAge.match(/^(<=|>=|<|>)\s*(\d+)/);
    if (opMatch) return { operator: opMatch[1], value: Number(opMatch[2]) };
    const numMatch = optAge.match(/(\d+)/);
    if (numMatch) return { operator: "==", value: Number(numMatch[0]) };
    return null;
  };

  const normalizeAgeOp = (op: string) =>
    op === "<" || op === "<=" ? "<" : op === ">" || op === ">=" ? ">" : op;

  const isAgeOptionMatch = (optAge: string, ageRule: any): boolean => {
    if (!optAge || optAge === "All") return true;
    if (!ageRule) return true;
    const parsed = parseOptAge(optAge);
    if (!parsed) return false;
    if (parsed.operator === "range" && ageRule.operator === "range") {
      return parsed.min === ageRule.min_value && parsed.max === ageRule.max_value;
    }
    return (
      normalizeAgeOp(parsed.operator) === normalizeAgeOp(ageRule.operator) &&
      parsed.value === ageRule.value
    );
  };
  // ---
  const [selectedRule, setSelectedRule] = useState("");
  const [selectedLabResult, setSelectedLabResult] = useState("");

  const currentSex = useMemo(() => {
    if (addTreeId && Array.isArray(props.SegmentTargetSequence?.listSegments)) {
      if (!props.SegmentTargetSequence?.listSegmentTrees?.some((i) => i.id === addTreeId)) {
        const branch = findBranchNodes(props.SegmentTargetSequence?.listSegments, addTreeId);
        const sexSegment = [...branch.parents, branch.child].find(
          (seg) => seg?.data?.rule?.field === "sex",
        );
        if (sexSegment) {
          return String(sexSegment.data?.rule?.value).toLowerCase();
        }
      }
    }
    return "";
  }, [
    addTreeId,
    props.SegmentTargetSequence?.listSegments,
    props.SegmentTargetSequence?.listSegmentTrees,
  ]);

  const currentAge = useMemo(() => {
    if (addTreeId && Array.isArray(props.SegmentTargetSequence?.listSegments)) {
      if (!props.SegmentTargetSequence?.listSegmentTrees?.some((i) => i.id === addTreeId)) {
        const branch = findBranchNodes(props.SegmentTargetSequence?.listSegments, addTreeId);
        const ageSegment = [...branch.parents, branch.child].find(
          (seg) => seg?.data?.rule?.field === "age",
        );
        if (ageSegment) {
          return ageSegment.data?.rule ?? null;
        }
      }
    }
    return null;
  }, [
    addTreeId,
    props.SegmentTargetSequence?.listSegments,
    props.SegmentTargetSequence?.listSegmentTrees,
  ]);

  //
  const labResultOptions = useMemo(() => {
    if (addTreeId && Array.isArray(props.SegmentTargetSequence?.listSegments)) {
      // parents: parents,
      // siblings: siblings,
      // child: childNode
      let labs: any[] = [];

      if (props.SegmentTargetSequence?.listSegmentTrees?.some((i) => i.id === addTreeId)) {
        labs = props.SegmentTargetSequence?.listSegments?.filter(
          (i) => i.data?.parent_segment_id === addTreeId,
        );
      } else {
        let branch = findBranchNodes(props.SegmentTargetSequence?.listSegments, addTreeId);
        labs = [...branch.parents, ...branch.siblings, branch.child];
      }

      labs = labs?.flatMap((i: any) => i?.data?.rule?.lab_test_master_id)?.filter(Boolean);
      console.log("labs: ", labs);

      // Helper: parse optAge string → { operator, value?, min?, max? }
      const parseOptAge = (optAge: string) => {
        if (!optAge || optAge === "All") return null;
        const rangeMatch = optAge.match(/(\d+)\s*[-–]\s*(\d+)/);
        if (rangeMatch)
          return { operator: "range", min: Number(rangeMatch[1]), max: Number(rangeMatch[2]) };
        const opMatch = optAge.match(/^(<=|>=|<|>)\s*(\d+)/);
        if (opMatch) return { operator: opMatch[1], value: Number(opMatch[2]) };
        const numMatch = optAge.match(/(\d+)/);
        if (numMatch) return { operator: "==", value: Number(numMatch[0]) };
        return null;
      };

      // Helper: check if a condition_option's age matches the segment's age rule
      const isAgeOptionMatch = (optAge: string): boolean => {
        if (!optAge || optAge === "All") return true; // "All" is a wildcard
        if (!currentAge) return true;
        const parsed = parseOptAge(optAge);
        if (!parsed) return false;
        if (parsed.operator === "range" && currentAge.operator === "range") {
          return parsed.min === currentAge.min_value && parsed.max === currentAge.max_value;
        }
        // Match <, >, <=, >= — treat < and <= as equivalent, > and >= as equivalent
        const normalize = (op: string) =>
          op === "<" || op === "<=" ? "<" : op === ">" || op === ">=" ? ">" : op;
        if (
          normalize(parsed.operator) === normalize(currentAge.operator) &&
          parsed.value === currentAge.value
        )
          return true;
        return false;
      };

      // Helper: check if a template has at least one option matching the current age
      const isAgeMatchTemplate = (template: any): boolean => {
        if (!currentAge) return true; // no age filter, allow all
        const opts: any[] = template.data?.template?.condition_options ?? [];
        if (opts.length === 0) return true;
        // Check if template has any age-specific options at all
        const hasAgeSpecificOpts = opts.some((opt: any) => opt?.age && String(opt.age) !== "All");
        // If template has no age-specific options, it's a universal template — allow it
        if (!hasAgeSpecificOpts) return true;
        // Otherwise, must have at least one option that matches the current age
        return opts.some((opt: any) => isAgeOptionMatch(String(opt?.age || "All")));
      };

      let options: any[] = (props.SegmentTargetSequence?.ruleTemplates || [])
        ?.filter((i) => !!i.data?.is_active)
        ?.filter((i) => i.data?.rule_type === "labResult")
        ?.filter((i) => !labs?.includes(i.data?.lab_test_master_id))
        ?.filter((i) => isAgeMatchTemplate(i))
        ?.map((i) => ({ text: i.data?.name, key: i.id, value: i.id }));
      return options;
    }

    return [];
  }, [
    props.SegmentTargetSequence?.listSegments,
    props.SegmentTargetSequence?.ruleTemplates,
    addTreeId,
    currentSex,
    currentAge,
  ]);

  const ruleTemplatesOptions = useMemo(() => {
    if (addTreeId && Array.isArray(props.SegmentTargetSequence?.listSegments)) {
      console.log("ruleTemplatesOptions props.SegmentTargetSequence", props.SegmentTargetSequence);

      let field: any[] = [];
      if (props.SegmentTargetSequence?.listSegmentTrees?.some((i) => i.id === addTreeId)) {
        field = props.SegmentTargetSequence?.listSegments?.filter(
          (i) => i.data?.parent_segment_id === addTreeId,
        );
      } else {
        let branch = findBranchNodes(props.SegmentTargetSequence?.listSegments, addTreeId);
        field = [...branch.parents, ...branch.siblings, branch.child];
      }

      field = field?.flatMap((i: any) => i?.data?.rule?.field)?.filter(Boolean);

      let options: any[] = (props.SegmentTargetSequence?.ruleTemplates || [])
        ?.filter((i) => !!i.data?.is_active)
        ?.filter((i) => i.data?.rule_type !== "labResult")
        ?.filter((i) => !field?.includes(i.data?.field))
        ?.map((i) => ({ text: i.data?.name, key: i.id, value: i.id }));
      if (labResultOptions?.length > 0) {
        options?.push({ text: "รายการตรวจ Lab", key: "LAB_RESULT", value: "LAB_RESULT" });
      }
      return options;
    }
    return [];
  }, [
    props.SegmentTargetSequence?.listSegments,
    props.SegmentTargetSequence?.ruleTemplates,
    labResultOptions,
    addTreeId,
  ]);

  const currentRuleTemplate = useMemo(() => {
    if (selectedRule && selectedRule !== "LAB_RESULT") {
      return props.SegmentTargetSequence?.ruleTemplates?.find((i) => i.id === selectedRule);
    } else if (selectedRule && selectedRule === "LAB_RESULT" && selectedLabResult) {
      return props.SegmentTargetSequence?.ruleTemplates?.find((i) => i.id === selectedLabResult);
    } else {
      return null;
    }
  }, [selectedRule, selectedLabResult]);

  const [width, setWidth] = useState("100%");
  const sidebarRef = useRef<HTMLDivElement>(null);
  const fullContentRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  console.log("currentRuleTemplate: ", currentRuleTemplate);

  useEffect(() => {
    props.runSequence({ sequence: "SegmentTarget", restart: true });
    return () => {
      props.runSequence({ sequence: "SegmentTarget", clear: true });
    };
  }, [props.selectedDivisionId]);

  const handleMouseDown = (e: React.MouseEvent) => {
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing.current || !sidebarRef.current) return;

    const halfContent = fullContentRef.current
      ? fullContentRef.current?.getBoundingClientRect().width / 2
      : 700;
    // ปรับตามตำแหน่ง mouse
    const newWidth = e.clientX - sidebarRef.current.getBoundingClientRect().left;
    setWidth(`${Math.max(100, Math.min(newWidth, halfContent))}px`); // จำกัด min/max width
  };

  const handleMouseUp = () => {
    isResizing.current = false;
    document.body.style.cursor = "";
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    if (props.SegmentTargetSequence?.loading === false) {
      setOpenModelAddTree(undefined);
      setOpenModelTree(undefined);
      setOpenModelDeleteAllChild(undefined);
      setDisabledBtnModelDeleteAllChild(false);
      setDisabledBtnModelTree(false);
      setDisabledBtnModelAddTree(false);
    }
  }, [props.SegmentTargetSequence?.loading]);

  /**
   * สร้าง tree จาก flat array โดยใช้ parent_segment_id
   */

  const buildTreeFromSegments = (
    segments: SegmentItem[],
    selectSegmentId: string = "default-parent",
  ) => {
    // map id เป็น node ชั่วคราว (จะถูกปรับให้มี children ในขั้นตอนถัดไป)
    const idToNode: Record<
      string,
      BranchType & { _parent?: string | null; _seq?: number; _rule?: any }
    > = {};

    segments.forEach((seg: SegmentItem) => {
      idToNode[seg.id || "root"] = {
        id: seg.id || "root",
        label: seg.data.name,
        _parent: seg.data.parent_segment_id || selectSegmentId,
        _seq: seg.data.seq, // Store seq number from data object
        _rule: seg.data.rule, // Store rule data from data object
      };
    });

    // เก็บ root node
    const roots: BranchType[] = [];

    // สร้างโครงสร้างต้นไม้
    Object.values(idToNode).forEach((node) => {
      if (node._parent && idToNode[node._parent]) {
        // ถ้า parent มีอยู่ใน list
        const parent = idToNode[node._parent];
        if (!parent.children) parent.children = [];
        parent.children.push(node);
      } else {
        // ถ้าไม่มี parent = เป็น root
        roots.push(node);
      }
    });

    // // Helper function to sort children based on seq rules
    // const sortChildren = (
    //   nodes: (BranchType & { _parent?: string | null; _seq?: number; _rule?: any })[]
    // ): void => {
    //   nodes.forEach((node) => {
    //     if (node.children && node.children.length > 0) {
    //       // Sort children based on seq rules
    //       node.children.sort((a: any, b: any) => {
    //         const aRule = a._rule || {};
    //         const bRule = b._rule || {};
    //         const aSeq = a._seq || 0;
    //         const bSeq = b._seq || 0;

    //         // Group 1: Objects with rule.field
    //         const aHasField = aRule.field !== undefined;
    //         const bHasField = bRule.field !== undefined;

    //         // Group 2: Objects with rule.lab_test_master_id (no field)
    //         const aHasLabTest = !aHasField && aRule.lab_test_master_id !== undefined;
    //         const bHasLabTest = !bHasField && bRule.lab_test_master_id !== undefined;

    //         // Priority 1: Objects with field come first
    //         if (aHasField && !bHasField) return -1;
    //         if (!aHasField && bHasField) return 1;

    //         // Both have field: sort by field name first, then by seq
    //         if (aHasField && bHasField) {
    //           const fieldCompare = aRule.field.localeCompare(bRule.field);
    //           if (fieldCompare !== 0) return fieldCompare; // Different fields
    //           return aSeq - bSeq; // Same field, sort by seq
    //         }

    //         // Priority 2: Objects with lab_test_master_id (when no field)
    //         if (aHasLabTest && !bHasLabTest) return -1;
    //         if (!aHasLabTest && bHasLabTest) return 1;

    //         // Both have lab_test_master_id: sort by lab_test_master_id first, then by seq
    //         if (aHasLabTest && bHasLabTest) {
    //           const labTestCompare = String(aRule.lab_test_master_id).localeCompare(
    //             String(bRule.lab_test_master_id)
    //           );
    //           if (labTestCompare !== 0) return labTestCompare; // Different lab test IDs
    //           return aSeq - bSeq; // Same lab test ID, sort by seq
    //         }

    //         // Fallback: sort by seq only
    //         return aSeq - bSeq;
    //       });

    //       // Recursively sort children's children
    //       sortChildren(node.children);
    //     }
    //   });
    // };

    // // Sort all children arrays
    // sortChildren(Object.values(idToNode));
    console.log("buildTreeFromSegments idToNode: ", idToNode);
    console.log("buildTreeFromSegments roots: ", roots);

    // Clean up function to remove temporary properties
    const stripParentProp = (nodes: BranchType[]): BranchType[] => {
      return nodes
        .sort((a, b) => a._seq - b._seq)
        .map((item: BranchType) => ({
          id: item.id,
          label: item.label,
          ...(item.children ? { children: stripParentProp(item.children) } : {}),
        }));
    };

    return stripParentProp(roots);
  };

  const handleConfirm = (type: typeAction) => {
    console.log("handleConfirm: type: ", type);

    if (type === "NewTreeSegment" || type === "EditNameTreeSegment") {
      setDisabledBtnModelTree(true);
    } else if (type === "AddSegment") {
      setDisabledBtnModelAddTree(true);
    } else if (type === "DeleteAllChildSegment") {
      setDisabledBtnModelDeleteAllChild(true);
    }
    props.runSequence({
      sequence: "SegmentTarget",
      action: type,
      ...(type === "NewTreeSegment" && { name: segmentName, description: segmentDescription }),
      ...(type === "EditNameTreeSegment" && {
        id: openModelTree?.id,
        name: segmentName,
        description: segmentDescription,
        // data: { ...openModelTree?.data?.data, name: data },
      }),
      ...(type === "AddSegment" && {
        parentId: openModelAddTree?.id,
        ruleTemplateId: currentRuleTemplate?.id,
        description: segmentDescription,
      }),
      ...(type === "DeleteAllChildSegment" && {
        parentId: openModelDeleteAllChild?.id,
      }),
    });
    setSegmentDescription("");
    setSegmentName("");
    setSelectedRule("");
    setSelectedLabResult("");
  };

  const listTrees = useMemo(() => {
    console.log("CardSegmentTargetManage useMemo Check props:", props);
    // console.log("CardSegmentTargetManage useMemo Check selectSegment:", selectedSegmentTreeId);
    if (
      (props.SegmentTargetSequence?.listSegmentTrees || []).length > 0 &&
      props.SegmentTargetSequence?.selectedSegmentTreeId
    ) {
      const filteredSegments = (props.SegmentTargetSequence?.listSegments || []).filter(
        (seg) => seg.data.segment_tree_id === props.SegmentTargetSequence?.selectedSegmentTreeId,
      );
      const benchList = buildTreeFromSegments(filteredSegments);
      console.log(
        "CardSegmentTargetManage listTrees props.SegmentTargetSequence?.listSegments: ",
        props.SegmentTargetSequence?.listSegments,
      );
      console.log("CardSegmentTargetManage listTrees benchList: ", benchList);
      const ROOT = props.SegmentTargetSequence?.listSegmentTrees?.find(
        (i) => i.id === props.SegmentTargetSequence?.selectedSegmentTreeId,
      )?.data;
      const fullTrees: BranchType = {
        id: String(props.SegmentTargetSequence?.selectedSegmentTreeId) || "root",
        label: ROOT?.name || "root",
        description: ROOT?.description || "",
        ...(benchList.length > 0 && { children: benchList }),
      };
      console.log("CardSegmentTargetManage listTrees fullTrees: ", fullTrees);
      return [fullTrees];
    } else {
      return [];
    }
  }, [
    props.SegmentTargetSequence?.listSegmentTrees,
    props.SegmentTargetSequence?.listSegments,
    props.SegmentTargetSequence?.selectedSegmentTreeId,
  ]);
  console.log("CardSegmentTargetManage useMemo listTrees: ", listTrees);

  const flattenObjects = (obj: any) => {
    const result: any[] = [];

    const traverse = (node: any) => {
      // Create a copy of the node without the children property
      const { children, ...nodeWithoutChildren } = node;
      result.push(nodeWithoutChildren);

      // Recursively process children
      if (children && Array.isArray(children)) {
        children.forEach((child) => traverse(child));
      }
    };

    traverse(obj);
    return result;
  };

  const handleEditTress = (id: string) => {
    console.log("handleEditTress props.SegmentTargetSequence: ", props.SegmentTargetSequence);
    let flat = flattenObjects(listTrees?.[0]);
    const listTreeItem = (flat || []).find((item: ListSegmentTreesType) => item.id === id);
    console.log("handleEditTress flat: ", flat);
    setSegmentName(listTreeItem.label);
    setSegmentDescription(listTreeItem.description);
    setOpenModelTree({
      type: "EditNameTreeSegment",
      id: listTreeItem?.id,
      description: listTreeItem?.description,
    });
  };

  const handleSelectedSegmentId = (id: string) => {
    console.log("handleAddSegment props.SegmentTargetSequence: id : ", id);
    console.log("handleAddSegment props.SegmentTargetSequence: ", props.SegmentTargetSequence);

    props.runSequence({
      sequence: "SegmentTarget",
      action: "SelectSegment",
      id,
    });
    // setSelectedSegmentId(id );
  };

  const handleAddSegment = (id: string) => {
    console.log("handleAddSegment props.SegmentTargetSequence: id : ", id);
    console.log("handleAddSegment props.SegmentTargetSequence: ", props.SegmentTargetSequence);
    setOpenModelAddTree({ type: "handleAddSegment", id: id });
    setAddTreeId(id);
  };
  const handleDeleteChildSegment = (id: string) => {
    console.log("setOpenModelDeleteAllChild props.SegmentTargetSequence: id : ", id);
    console.log(
      "setOpenModelDeleteAllChild props.SegmentTargetSequence: ",
      props.SegmentTargetSequence,
    );
    setOpenModelDeleteAllChild({ type: "handleDeleteChildSegment", id: id });
  };

  const handleCloseModal = () => {
    setOpenModelTree(undefined);
    setSegmentDescription("");
    setSegmentName("");
    setDisabledBtnModelTree(false);
  };

  const handleCloseAddModal = () => {
    setOpenModelAddTree(undefined);
    setSegmentDescription("");
    setSelectedRule("");
    setSelectedLabResult("");
    setDisabledBtnModelAddTree(false);
    setAddTreeId("");
  };

  const handleCloseDeleteModal = () => {
    setOpenModelDeleteAllChild(undefined);
    setDisabledBtnModelDeleteAllChild(false);
  };

  console.log("CardSegmentTargetManage props: ", props);
  return (
    <div
      style={{ display: "flex", width: "100%", height: "calc(100vh - 40px)", position: "relative" }}
    >
      <Dimmer active={props.SegmentTargetSequence?.loadingStatus?.[UploadRuleTemplates]} inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>
      <SnackMessage
        error={props.SegmentTargetSequence?.error}
        success={null}
        onClose={() => {
          props.setProp("SegmentTargetSequence.error", "");
        }}
      />
      <ThreePanelSplitView
        runSequence={props.runSequence}
        leftPanel={
          <div style={{ width: "100%", borderRight: "1px solid #FFC4C4" }}>
            <span
              style={STYLES.nodeContainer}
              onClick={() => {
                console.log(" New TreeSegment");
                setOpenModelTree({ type: "NewTreeSegment" });
              }}
            >
              <div>{"สร้าง Target"}</div>
              <Image
                src={ICON.addSegment}
                style={{ marginLeft: "13px", width: "20px", height: "20px" }}
              />
            </span>
            {(props.SegmentTargetSequence?.listSegmentTrees || []).map(
              (item: ListSegmentTreesType, idx: number) => (
                <Popup
                  key={item.id}
                  content={`Created : ${moment(
                    (item.data.created_at as Timestamp)?.toDate(),
                  ).format("YYYY/MM/DD HH:mm")}`}
                  trigger={
                    <div
                      onClick={() => {
                        console.log("item", item);
                        props.runSequence({
                          sequence: "SegmentTarget",
                          action: "querySegmentFromSegmentTreesId",
                          id: item?.id,
                        });
                      }}
                      style={{
                        ...STYLES.nodeLabel,
                        ...(item.id === props.SegmentTargetSequence?.selectedSegmentTreeId && {
                          background: "#FFE6E6",
                        }),
                      }}
                    >
                      {item.data.name}
                      {item.data.created_by === props.userId ||
                      item.data.divisionId === props.selectedDivisionId ? (
                        <Icon name="edit" color="pink" />
                      ) : null}
                    </div>
                  }
                />
              ),
            )}
          </div>
        }
        middlePanel={
          <div
            ref={sidebarRef}
            style={{
              width: "100%",
              position: "relative",
              overflow: "auto",
              padding: "10px",
              height: "100%",
              transition: isResizing.current ? "none" : "width 0.2s",
            }}
          >
            {/* ...sidebar content... */}
            {(props.SegmentTargetSequence?.listSegmentTrees || []).length > 0 ? (
              <>
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: 8,
                    cursor: "col-resize",
                    zIndex: 10,
                  }}
                  onMouseDown={handleMouseDown}
                />
                <SegmentTargetTree
                  listTrees={listTrees}
                  onEditTress={(parentId: string) => handleEditTress(parentId)}
                  onAddNode={(parentId: string) => handleAddSegment(parentId)}
                  onRemove={(parentId: string) => handleDeleteChildSegment(parentId)}
                  onSelectedSegmentId={(id: string) => handleSelectedSegmentId(id)}
                  SegmentTargetSequence={props.SegmentTargetSequence}
                  // selectedSegmentId={selectedSegmentId}
                />
              </>
            ) : null}
          </div>
        }
        rightPanel={
          <div ref={fullContentRef} style={{ display: "flex", width: "100%", padding: "10px 0px" }}>
            {/* Sidebar */}

            {/* Content */}
            {props.SegmentTargetSequence?.selectedSegmentId &&
            props.SegmentTargetSequence?.selectedSegmentId !==
              props.SegmentTargetSequence?.selectedSegmentTreeId ? (
              <div style={{ display: "flex", width: "100%", padding: "4px" }}>
                <CardSegmentEditContent
                  setProp={props.setProp}
                  SegmentTargetSequence={props.SegmentTargetSequence}
                  runSequence={props.runSequence}
                  setModalParam={setModalParam}
                  languageUX={props.languageUX}
                />
              </div>
            ) : null}
          </div>
        }
      />

      <Modal
        open={!!openModelTree}
        closeIcon
        onClose={handleCloseModal}
        style={{ width: "40%", borderRadius: "16px" }}
      >
        <ModalHeader>
          {openModelTree?.type === "EditNameTreeSegment"
            ? "แก้ไขชื่อกลุ่มเป้าหมาย"
            : "สร้างรายการใหม่"}
        </ModalHeader>
        <ModalContent>
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "22px",
              width: "100%",
              flexDirection: "column",
            }}
          >
            {openModelTree?.type === "NewTreeSegment" ? (
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                กรุณาระบุ กลุ่มเป้าหมาย<span style={{ color: "red" }}>*</span>
              </div>
            ) : (
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                แก้ไข กลุ่มเป้าหมาย<span style={{ color: "red" }}>*</span>
              </div>
            )}
            <Input
              style={{ fontSize: "16px", fontWeight: "bold" }}
              value={segmentName}
              placeholder={
                // TODO: Check openModalTree two case
                openModelTree?.type === "EditNameTreeSegment"
                  ? openModelTree.data?.data?.name || "ระบุชื่อเงื่อนไข"
                  : "ระบุชื่อเงื่อนไข"
              }
              onChange={(e: any, d: any) => {
                setSegmentName(d.value);
              }}
            />

            {openModelTree?.type === "NewTreeSegment" ? (
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>กรุณาระบุ รายละเอียด</div>
            ) : (
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>แก้ไข รายละเอียด</div>
            )}
            <Input
              style={{ fontSize: "16px", fontWeight: "bold" }}
              value={segmentDescription}
              placeholder={
                openModelTree?.type === "EditNameTreeSegment"
                  ? // TODO : check this data ? on two case
                    openModelTree.data?.data?.name || "ระบุรายละเอียด"
                  : "ระบุชื่อรายละเอียด"
              }
              onChange={(e: any, d: any) => {
                setSegmentDescription(d.value);
              }}
            />
          </div>
        </ModalContent>
        <ModalActions>
          <Button disabled={disabledBtnModelTree} color="grey" onClick={handleCloseModal}>
            ยกเลิก
          </Button>
          <Button
            disabled={disabledBtnModelTree || !segmentName}
            color="pink"
            onClick={() => handleConfirm(openModelTree?.type || "NewTreeSegment")}
          >
            บันทึก
          </Button>
        </ModalActions>
      </Modal>

      <Modal
        open={!!openModelAddTree}
        closeIcon
        onClose={handleCloseAddModal}
        style={{ width: "40%", borderRadius: "16px" }}
      >
        <ModalHeader></ModalHeader>
        <ModalContent>
          <div style={{ marginBottom: "6px" }}>
            <div>รายละเอียด</div>
            <Input
              fluid
              style={{ fontSize: "16px", fontWeight: "bold" }}
              value={segmentDescription}
              onChange={(e: any, d: any) => {
                setSegmentDescription(d.value);
              }}
            />
          </div>

          <div>
            <div style={{ marginBottom: "6px" }}>
              ระบุตัวเลือก สำหรับกำหนดเงื่อนไข <span style={{ color: "red" }}>*</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                gap: "22px",
                width: "100%",
                flexDirection: "column",
              }}
            >
              <Dropdown
                options={ruleTemplatesOptions}
                selection
                search
                value={selectedRule}
                onChange={(e, data: DropdownProps) => {
                  setSelectedRule(data.value as string);
                }}
              />
            </div>
          </div>
          {selectedRule === "LAB_RESULT" ? (
            <div>
              <div style={{ marginBottom: "6px" }}>
                ระบุรายการตรวจ <span style={{ color: "red" }}>*</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  gap: "22px",
                  width: "100%",
                  flexDirection: "column",
                }}
              >
                <Dropdown
                  options={labResultOptions}
                  selection
                  search
                  value={selectedLabResult}
                  onChange={(e, data: DropdownProps) => {
                    setSelectedLabResult(data.value as string);
                  }}
                />
              </div>
            </div>
          ) : null}

          {Array.isArray(currentRuleTemplate?.data?.template?.condition_options) ? (
            <List as="ul">
              {currentRuleTemplate?.data?.template?.condition_options
                .filter((i: any) => {
                  // Filter by sex
                  if (currentSex) {
                    const optSex = String(i.sex || "all").toLowerCase();
                    if (optSex !== "all" && optSex !== currentSex) return false;
                  }
                  // Filter by age (exact match via shared helper)
                  if (currentAge) {
                    if (!isAgeOptionMatch(String(i.age || "All"), currentAge)) return false;
                  }
                  return true;
                })
                .flatMap((i) => (
                  <ListItem as="li" key={i.id || i.text}>
                    {i.text}
                  </ListItem>
                ))}
            </List>
          ) : null}
        </ModalContent>
        <ModalActions>
          <Button disabled={disabledBtnModelAddTree} color="grey" onClick={handleCloseAddModal}>
            ยกเลิก
          </Button>
          <Button
            disabled={disabledBtnModelAddTree || !currentRuleTemplate}
            color="pink"
            onClick={() => handleConfirm("AddSegment")}
          >
            บันทึก
          </Button>
        </ModalActions>
      </Modal>

      <Modal
        open={!!openModelDeleteAllChild}
        closeIcon
        onClose={handleCloseDeleteModal}
        style={{ width: "40%", borderRadius: "16px" }}
      >
        <ModalHeader>ยืนยันลบเงื่อนไขที่สร้างไว้</ModalHeader>
        <ModalContent>{`ยืนยันลบรายการภายใน Segment: ${
          props.SegmentTargetSequence?.listSegments?.find(
            (i) => openModelDeleteAllChild?.id === i.id,
          )
            ? props.SegmentTargetSequence?.listSegments?.find(
                (i) => openModelDeleteAllChild?.id === i.id,
              )?.data?.name
            : props.SegmentTargetSequence?.listSegmentTrees?.find(
                (i) => openModelDeleteAllChild?.id === i.id,
              )?.data?.name
        } ทั้งหมดหรือไม่`}</ModalContent>
        <ModalActions>
          <Button
            disabled={disabledBtnModelDeleteAllChild}
            color="grey"
            onClick={handleCloseDeleteModal}
          >
            ยกเลิก
          </Button>
          <Button
            disabled={disabledBtnModelDeleteAllChild}
            color="pink"
            onClick={() => handleConfirm("DeleteAllChildSegment")}
          >
            ยืนยันลบ
          </Button>
        </ModalActions>
      </Modal>

      <Modal
        open={!!modalParam?.open}
        closeIcon
        onClose={() => {
          setModalParam(undefined);
        }}
        style={{ width: "40%", borderRadius: "16px" }}
      >
        <ModalHeader>{modalParam?.header}</ModalHeader>
        <ModalContent>{modalParam?.content}</ModalContent>
        <ModalActions>
          <Button color="grey" onClick={modalParam?.cancle}>
            {modalParam?.cancleText}
          </Button>
          <Button color="pink" onClick={modalParam?.confirm}>
            {modalParam?.confirmText}
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
};

const findBranchNodes = (rootId: SegmentItem[], childId: string) => {
  // Helper function to find a node by ID
  function findNodeById(id: string) {
    return rootId.find((segment) => segment.id === id);
  }

  // Helper function to find all children of a parent
  function findChildrenByParentId(parentId: string) {
    return rootId.filter((segment) => segment.data.parent_segment_id === parentId);
  }

  // Find the target child node
  const childNode = findNodeById(childId);
  if (!childNode) {
    return { parents: [], siblings: [], error: "Child node not found" };
  }

  // Collect all parents by traversing up the tree
  const parents = [];
  let currentNode = childNode;

  while (
    currentNode &&
    currentNode.data.parent_segment_id !== null &&
    currentNode.data.parent_segment_id !== undefined
  ) {
    const parentNode = findNodeById(currentNode.data.parent_segment_id);
    if (parentNode) {
      parents.unshift(parentNode); // Add to beginning to maintain hierarchy order
      currentNode = parentNode;
    } else {
      break;
    }
  }

  // Find siblings (nodes with same parentId, excluding the child itself)
  const siblings = [];
  if (childNode.data.parent_segment_id !== null && childNode.data.parent_segment_id !== undefined) {
    const allSiblings = findChildrenByParentId(childNode.data.parent_segment_id);
    siblings.push(...allSiblings.filter((segment) => segment.id !== childId));
  }

  return {
    parents: parents,
    siblings: siblings,
    child: childNode,
  };
};

CardSegmentTargetManage.displayName = "CardSegmentTargetManage";

export default React.memo(CardSegmentTargetManage);
